import { useTranslation } from "react-i18next";
import SvgIcon from "../SvgIcon";
import { ZOOM_STEP } from "./usePairCompareState";

const ICON_SIZE = { width: "1rem", height: "1rem" };

// A labelled magnifier −/+ pair driving one photo's zoom.
const ZoomStepper = ({
  label,
  zoomInLabel,
  zoomOutLabel,
  onZoomOut,
  onZoomIn,
}) => (
  <span className="compare-zoom-stepper">
    <span className="compare-zoom-label">{label}</span>
    <span className="compare-zoom-buttons">
      <button
        type="button"
        onClick={onZoomOut}
        aria-label={`${zoomOutLabel} (${label})`}
      >
        <SvgIcon
          src="/img/new-icons/Zoom-out.svg"
          useCurrentColor
          style={ICON_SIZE}
        />
      </button>
      <button
        type="button"
        onClick={onZoomIn}
        aria-label={`${zoomInLabel} (${label})`}
      >
        <SvgIcon
          src="/img/new-icons/Zoom.svg"
          useCurrentColor
          style={ICON_SIZE}
        />
      </button>
    </span>
  </span>
);

// The alignment toolbar for the slider and fade stages: a zoom stepper per
// photo, the fade-only Adjust toggle and Opacity slider, and Reset.
//
// `stacked` puts each caption above its control and titles the opacity slider —
// the arrangement used when the toolbar sits in the modal's top control row.
// Without it the captions sit inline, which is how it reads under the stage.
const PairCompareControls = ({ mode, left, right, state, stacked }) => {
  const { t } = useTranslation("athlete-portal");
  const {
    stepZoom,
    resetScope,
    isAnyAdjusted,
    fade,
    setFade,
    fadeSide,
    setFadeSide,
  } = state;

  if (mode !== "slider" && mode !== "fade") return null;

  // Dates make good stage tags but blow out the toolbar, so callers can pass a
  // short caption for the controls and keep the long one on the photo.
  const leftLabel = left.controlLabel || left.label;
  const rightLabel = right.controlLabel || right.label;

  const zoomInLabel = t("questionProgress.zoomIn");
  const zoomOutLabel = t("questionProgress.zoomOut");

  // Rules between the sections, drawn only in the framed (top-row) variant
  // where the toolbar is one panel holding several distinct controls.
  const divider = stacked ? (
    <span className="compare-divider" aria-hidden="true" />
  ) : null;

  return (
    <div
      className={
        stacked
          ? "compare-align-bar is-stacked compare-panel"
          : "compare-align-bar"
      }
    >
      {mode === "fade" ? (
        <span className="compare-adjust-toggle">
          <span className="compare-zoom-label">
            {t("questionProgress.adjust")}
          </span>
          {/* The label wears its own element so the selected segment can carry
              both a chip background and gradient lettering — see the CSS. */}
          <span className="compare-adjust-buttons">
            <button
              type="button"
              className={fadeSide === "left" ? "active" : ""}
              onClick={() => setFadeSide("left")}
            >
              <span className="compare-segment-label">{leftLabel}</span>
            </button>
            <button
              type="button"
              className={fadeSide === "right" ? "active" : ""}
              onClick={() => setFadeSide("right")}
            >
              <span className="compare-segment-label">{rightLabel}</span>
            </button>
          </span>
        </span>
      ) : null}

      {mode === "fade" ? divider : null}

      <ZoomStepper
        label={leftLabel}
        zoomInLabel={zoomInLabel}
        zoomOutLabel={zoomOutLabel}
        onZoomOut={() => stepZoom("left", -ZOOM_STEP)}
        onZoomIn={() => stepZoom("left", ZOOM_STEP)}
      />
      <ZoomStepper
        label={rightLabel}
        zoomInLabel={zoomInLabel}
        zoomOutLabel={zoomOutLabel}
        onZoomOut={() => stepZoom("right", -ZOOM_STEP)}
        onZoomIn={() => stepZoom("right", ZOOM_STEP)}
      />

      {mode === "fade" ? (
        <span className="fade-control">
          {stacked ? (
            <span className="compare-zoom-label">
              {t("questionProgress.opacity")}
            </span>
          ) : null}
          {stacked ? null : <span>{leftLabel}</span>}
          <input
            type="range"
            min="0"
            max="100"
            value={fade}
            onChange={(e) => setFade(Number(e.target.value))}
            style={{ "--fade-fill": `${fade}%` }}
            aria-label={t("questionProgress.fade")}
          />
          {stacked ? (
            <span className="fade-ends">
              <span>{leftLabel}</span>
              <span>{rightLabel}</span>
            </span>
          ) : (
            <span>{rightLabel}</span>
          )}
        </span>
      ) : null}

      {divider}

      <button
        type="button"
        className="compare-align-reset"
        onClick={resetScope}
        disabled={!isAnyAdjusted}
      >
        {t("questionProgress.resetZoom")}
      </button>
    </div>
  );
};

export default PairCompareControls;
